import { useEffect, useState } from "react";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  KeyRound, Plus, Copy, RefreshCw, Trash2, CheckCircle2, XCircle,
  ShieldOff, Loader2, X, Info, AlertTriangle,
} from "lucide-react";

// ------- Reveal-once card ---------------------------------------------
function NewKeyRevealCard({ payload, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — select the token and copy manually."); }
  };
  return (
    <div className="p-4 rounded-2xl bg-[#FEF3C7] border border-[#FCD34D]"
         data-testid="governance-key-reveal">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#92400E] mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#92400E]">
            Copy this key now — it will NEVER be shown again.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate text-[11px] font-mono bg-white border border-[#FCD34D] rounded-lg px-2 py-1.5"
                  data-testid="governance-key-token">
              {payload.token}
            </code>
            <button type="button" onClick={copy}
                    className="px-3 py-1.5 rounded-lg bg-[#1E3A8A] text-white text-xs flex items-center gap-1.5"
                    data-testid="governance-key-copy">
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="mt-2 text-xs text-[#92400E]">
            Name: <span className="font-mono">{payload.name}</span> · Prefix: <span className="font-mono">{payload.prefix}</span>
            {payload.rate_limit_per_min ? <> · Rate limit: {payload.rate_limit_per_min}/min</> : null}
          </div>
        </div>
        <button type="button" onClick={onDismiss}
                className="text-[#92400E] hover:text-[#78350F]"
                data-testid="governance-key-reveal-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ------- Create dialog ------------------------------------------------
function CreateKeyDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [rateLimit, setRateLimit] = useState(300);
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(),
                        rate_limit_per_min: Number(rateLimit) || 300 };
      if (expiresAt) payload.expires_at = new Date(expiresAt + "T23:59:59").toISOString();
      const { data } = await api.post("/admin/governance/keys", payload);
      toast.success(`Key "${data.name}" created.`);
      onCreated(data);
      onClose();
      setName(""); setDescription(""); setExpiresAt(""); setRateLimit(300);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="governance-key-create-dialog">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#E5E5E0] flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-[#1E3A8A] mt-1" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#1E3A8A]">Governance</div>
            <h2 className="text-lg font-semibold text-slate-900">New API Key</h2>
            <p className="text-xs text-slate-500 mt-1">
              Read-only, single-scope key for Power BI / SIEM ingestion. Plaintext
              is shown exactly once at creation.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800"
                  data-testid="governance-key-create-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <FormField label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Power BI · Compliance dashboard"
                   className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
                   data-testid="governance-key-create-name" />
          </FormField>
          <FormField label="Description (optional)">
            <textarea rows={2} value={description}
                       onChange={(e) => setDescription(e.target.value)}
                       placeholder="What system will use this key?"
                       className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg text-sm"
                       data-testid="governance-key-create-description" />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Expires (optional)">
              <input type="date" value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
                      data-testid="governance-key-create-expires" />
            </FormField>
            <FormField label="Rate limit (per min)">
              <input type="number" min={10} max={5000} value={rateLimit}
                      onChange={(e) => setRateLimit(e.target.value)}
                      className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
                      data-testid="governance-key-create-rate-limit" />
            </FormField>
          </div>
          <div className="p-3 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] text-xs text-[#1E3A8A]">
            <Info className="w-3.5 h-3.5 inline mr-1" />
            Scope: <span className="font-mono">read:governance</span> · Tenant: <span className="font-mono">harborwell</span>.
            The API supports both <span className="font-mono">X-Governance-API-Key</span> header
            and <span className="font-mono">Authorization: Bearer</span>. Same endpoints will
            be reachable via OAuth2 client credentials in a future wave.
          </div>
          <div className="pt-4 border-t border-[#E5E5E0] flex gap-2">
            <button type="button" onClick={submit} disabled={saving}
                    className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-50"
                    data-testid="governance-key-create-submit">
              {saving ? "Creating…" : "Create key"}
            </button>
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-lg border border-[#E5E5E0] text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
function FormField({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.14em] text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ------- Main page ----------------------------------------------------
export default function AdminGovernanceKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [reveal, setReveal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/governance/keys");
      setKeys(data.keys || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"?\n\nAny BI tool using this key will start receiving 401 responses immediately. This cannot be undone.`)) return;
    try {
      await api.post(`/admin/governance/keys/${k.id}/revoke`, { reason: "revoked from UI" });
      toast.success(`Key "${k.name}" revoked.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const regenerate = async (k) => {
    if (!window.confirm(`Regenerate "${k.name}"?\n\nThe existing key will be revoked immediately and a new one issued. BI tools must be updated with the new token.`)) return;
    try {
      const { data } = await api.post(`/admin/governance/keys/${k.id}/regenerate`);
      setReveal(data);
      toast.success(`Key "${data.name}" regenerated.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-16">
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <div className="p-6 rounded-3xl bg-white border border-[#E5E5E0] shadow-sm">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-[#1E3A8A]">
                Enterprise Governance · API Keys
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-1">Governance & Reporting API Keys</h1>
              <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                Rotatable, read-only API keys for HarborWell Connect&apos;s internal reporting and BI
                integrations (Power BI, SIEM, external reporting). Scoped to
                <span className="font-mono text-xs bg-[#F1F5F9] rounded px-1 py-0.5 mx-1">read:governance</span>.
                All lifecycle events are audit-logged.
              </p>
            </div>
            <button type="button" onClick={() => setCreateOpen(true)}
                    className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium flex items-center gap-2"
                    data-testid="governance-key-new">
              <Plus className="w-4 h-4" />
              New key
            </button>
            <button type="button" onClick={load}
                    className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm bg-white flex items-center gap-2"
                    data-testid="governance-key-refresh">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {reveal && (
          <div className="mt-6">
            <NewKeyRevealCard payload={reveal} onDismiss={() => setReveal(null)} />
          </div>
        )}

        <div className="mt-6">
          {loading ? (
            <div className="p-12 flex items-center justify-center border border-[#E5E5E0] rounded-2xl bg-white">
              <Loader2 className="w-5 h-5 animate-spin text-[#1E3A8A]" />
            </div>
          ) : keys.length === 0 ? (
            <div className="p-12 border border-[#E5E5E0] rounded-2xl bg-white text-center">
              <KeyRound className="w-10 h-10 mx-auto text-slate-400" />
              <p className="mt-3 text-sm text-slate-600">No API keys yet.</p>
              <p className="text-xs text-slate-500 mt-1">
                Create your first key to enable Power BI / SIEM ingestion.
              </p>
            </div>
          ) : (
            <ul className="border border-[#E5E5E0] rounded-2xl divide-y divide-[#E5E5E0] bg-white"
                data-testid="governance-key-list">
              {keys.map((k) => {
                const now = Date.now();
                const expired = k.expires_at && new Date(k.expires_at).getTime() < now;
                return (
                  <li key={k.id} className="p-4 flex items-center gap-4"
                      data-testid={`governance-key-row-${k.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 truncate">{k.name}</span>
                        <span className="font-mono text-[11px] text-slate-500">gv_{k.prefix}…</span>
                        {k.revoked ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold bg-[#F1F5F9] text-[#334155]">
                            <ShieldOff className="w-3 h-3 inline mr-1" />
                            Revoked
                          </span>
                        ) : expired ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold bg-[#FEE2E2] text-[#991B1B]">
                            <XCircle className="w-3 h-3 inline mr-1" />
                            Expired
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold bg-[#DCFCE7] text-[#166534]">
                            <CheckCircle2 className="w-3 h-3 inline mr-1" />
                            Active
                          </span>
                        )}
                        {(k.scopes || []).map((s) => (
                          <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#1E3A8A] font-mono">
                            {s}
                          </span>
                        ))}
                      </div>
                      {k.description && (
                        <div className="mt-1 text-xs text-slate-600 truncate">{k.description}</div>
                      )}
                      <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                        <span>Created by {k.created_by_email || "—"}</span>
                        <span>· {formatRelative(k.created_at)}</span>
                        {k.last_used_at ? (
                          <span>· Last used {formatRelative(k.last_used_at)}</span>
                        ) : (
                          <span>· Never used</span>
                        )}
                        <span>· {k.request_count || 0} requests</span>
                        {k.expires_at && (
                          <span>· Expires {new Date(k.expires_at).toLocaleDateString()}</span>
                        )}
                        {k.rate_limit_per_min && (
                          <span>· {k.rate_limit_per_min}/min</span>
                        )}
                      </div>
                    </div>
                    {!k.revoked && (
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => regenerate(k)}
                                className="px-3 py-1.5 rounded-lg border border-[#1E3A8A] text-[#1E3A8A] text-xs font-medium hover:bg-[#EFF6FF] flex items-center gap-1.5"
                                data-testid={`governance-key-regenerate-${k.id}`}>
                          <RefreshCw className="w-3.5 h-3.5" />
                          Regenerate
                        </button>
                        <button type="button" onClick={() => revoke(k)}
                                className="px-3 py-1.5 rounded-lg border border-[#991B1B] text-[#991B1B] text-xs font-medium hover:bg-[#FEE2E2] flex items-center gap-1.5"
                                data-testid={`governance-key-revoke-${k.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                          Revoke
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Documentation */}
        <div className="mt-8 p-6 rounded-2xl border border-[#E5E5E0] bg-white">
          <h2 className="text-sm font-semibold text-slate-900">Endpoint reference</h2>
          <p className="mt-1 text-xs text-slate-500">
            All endpoints under <code className="text-[11px] bg-[#F1F5F9] px-1 rounded">/api/v1/governance/*</code>
            require a valid key. Send it as <code className="text-[11px] bg-[#F1F5F9] px-1 rounded">X-Governance-API-Key: gv_…</code>
            or <code className="text-[11px] bg-[#F1F5F9] px-1 rounded">Authorization: Bearer gv_…</code>.
          </p>
          <ul className="mt-3 text-xs text-slate-700 space-y-1.5 font-mono">
            <li>GET  /api/v1/governance/health</li>
            <li>GET  /api/v1/governance/compliance/score</li>
            <li>GET  /api/v1/governance/compliance/issues?severity=&amp;category=&amp;can_auto_fix=</li>
            <li>GET  /api/v1/governance/legal-holds?status=</li>
            <li>GET  /api/v1/governance/exceptions?status=&amp;severity=&amp;assigned_owner_email=</li>
            <li>GET  /api/v1/governance/exceptions/metrics</li>
            <li>GET  /api/v1/governance/audit-completeness</li>
            <li>GET  /api/v1/governance/history?days=90</li>
          </ul>
        </div>
      </div>

      <CreateKeyDialog open={createOpen} onClose={() => setCreateOpen(false)}
                        onCreated={(k) => { setReveal(k); load(); }} />
    </div>
  );
}
